type StorageKind = 'localStorage' | 'sessionStorage';

/**
 * Storage facade that keeps the application usable when a browser denies
 * access to Web Storage (for example Safari privacy modes or embedded pages).
 */
class SafeWebStorage {
  private readonly fallback = new Map<string, string>();

  constructor(private readonly kind: StorageKind) {}

  getItem(key: string): string | null {
    if (typeof window === 'undefined') return null;

    try {
      const storage = this.storage;
      return storage ? storage.getItem(key) : this.fallback.get(key) ?? null;
    } catch {
      return this.fallback.get(key) ?? null;
    }
  }

  setItem(key: string, value: string): void {
    if (typeof window === 'undefined') return;

    const normalizedValue = String(value);
    this.fallback.set(key, normalizedValue);

    try {
      this.storage?.setItem(key, normalizedValue);
    } catch {
      // The in-memory value preserves behavior for the current page session.
    }
  }

  removeItem(key: string): void {
    if (typeof window === 'undefined') return;

    this.fallback.delete(key);

    try {
      this.storage?.removeItem(key);
    } catch {
      // Storage may be unavailable; the fallback was still cleared.
    }
  }

  private get storage(): Storage | null {
    if (typeof window === 'undefined') return null;

    try {
      return window[this.kind];
    } catch {
      return null;
    }
  }
}

export const safeLocalStorage = new SafeWebStorage('localStorage');
export const safeSessionStorage = new SafeWebStorage('sessionStorage');
